/**
 * DOC.GENERATED — Generated Documents queue/status list (§10 core surface).
 *
 * A read-only list of render requests (doc_generated). Consumers (no designer
 * licence) watch status, download a rendered artifact, and inspect run inputs.
 * Follows the platform new-page contract (§17): self-wraps PersonalizationProvider,
 * exposes a PersonalizationToggle, renders via PersonalizedGrid, filters with
 * free-text + FacetFilter + ClearFiltersButton. The template FK is resolved to its
 * name at fetch time (never the raw id); status / output_format render through a
 * single labelled-constant per enum; dates through the locale formatter.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import type { AuthSession } from '../../types/auth';
import { EmptyState } from '../../components/common/EmptyState';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Modal } from '../../components/common/Modal';
import { PdfViewer } from '../../components/common/PdfViewer';
import { Maximize2, Minimize2 } from 'lucide-react';
import { getSignedUrl } from '../../services/dms/DmsService';
import { Search, RefreshCw } from 'lucide-react';

const GENERATED_PAGE = 'DOC.GENERATED';

/** Sentinel for "the row points at no stored file", localized at the surface. */
const NO_ARTIFACT = 'DOC.GENERATED.NO_ARTIFACT';

/** A saved file named after the document, not after its surrogate id. */
function fileNameFor(r: { title: string | null; template_name: string; doc_generated_id: number; output_format: string | null }): string {
  const stem = (r.title || r.template_name || 'document')
    .replace(/[^\w\s.-]+/g, ' ')      // drop separators a filesystem dislikes
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120) || 'document';
  const ext = (r.output_format || 'pdf').toLowerCase();
  return `${stem} (${r.doc_generated_id}).${ext}`;
}

function messageFor(e: unknown, t: (k: string, d: string) => string): string {
  const raw = (e as Error).message;
  return raw === NO_ARTIFACT
    ? t('doc.generated.err.no_artifact', 'This document has no stored artifact to download.')
    : raw;
}

// ── CHECK-enum labelled constants (§17.B11) — stored value is the code; the
// label is localized and is the only thing rendered in cells/facets/options.
interface EnumOpt { value: string; labelKey: string; fallback: string; }

const STATUSES: EnumOpt[] = [
  { value: 'REQUESTED', labelKey: 'doc.gen_status.REQUESTED', fallback: 'Requested' },
  { value: 'RENDERED',  labelKey: 'doc.gen_status.RENDERED',  fallback: 'Rendered' },
  { value: 'FAILED',    labelKey: 'doc.gen_status.FAILED',    fallback: 'Failed' },
];

const OUTPUT_FORMATS: EnumOpt[] = [
  { value: 'PDF',  labelKey: 'doc.output_format.PDF',  fallback: 'PDF' },
  { value: 'HTML', labelKey: 'doc.output_format.HTML', fallback: 'HTML' },
  { value: 'XLSX', labelKey: 'doc.output_format.XLSX', fallback: 'Excel (XLSX)' },
  { value: 'CSV',  labelKey: 'doc.output_format.CSV',  fallback: 'CSV' },
];

interface GeneratedRow {
  doc_generated_id: number;
  title: string | null;
  template_name: string;   // resolved from doc_template.name / internal_code
  status: string;
  output_format: string | null;
  page_count: number | null;
  record_id: string | null;
  dms_document_id: number | null;
  created_at: string;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'title',         header: 'Title',   defaultWidth: 240 },
  { key: 'template_name', header: 'Template', defaultWidth: 220 },
  { key: 'status',        header: 'Status',  defaultWidth: 120 },
  { key: 'output_format', header: 'Format',  defaultWidth: 110 },
  { key: 'page_count',    header: 'Pages',   defaultWidth: 90 },
  { key: 'record_id',     header: 'Record',  defaultWidth: 140 },
  { key: 'created_at',    header: 'Requested', defaultWidth: 180 },
];

function GeneratedInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  useActiveLocale(); // re-render on locale switch (enum labels + dates follow it)
  const formatDateTime = useFormatDateTime();
  const detail = useExpandedRows<number>();
  const [rows, setRows] = useState<GeneratedRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<string[]>([]);
  const [formatFacet, setFormatFacet] = useState<string[]>([]);
  const [templateFacet, setTemplateFacet] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<{ row: GeneratedRow; url: string } | null>(null);
  const [expanded, setExpanded] = useState(false);

  const statusLabel = (v: string) => {
    const o = STATUSES.find(s => s.value === v);
    return o ? t(o.labelKey, o.fallback) : v;
  };
  const formatLabel = (v: string | null) => {
    if (!v) return '';
    const o = OUTPUT_FORMATS.find(s => s.value === v);
    return o ? t(o.labelKey, o.fallback) : v;
  };

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      // Resolve the template FK to its name at fetch time (§17.B4) — never show
      // the raw doc_template_id. doc_template carries `internal_code` + `name`.
      const { data, error: e } = await apiDb.from('doc_generated').select(`
        doc_generated_id, title, status, output_format, page_count, record_id,
        dms_document_id, created_at,
        doc_template ( internal_code, name )
      `).order('created_at', { ascending: false }).limit(500);
      if (e) throw e;
      type Raw = Omit<GeneratedRow, 'template_name'> & { doc_template?: { internal_code: string; name: string | null } | null };
      setRows(((data ?? []) as unknown as Raw[]).map(r => ({
        doc_generated_id: r.doc_generated_id,
        title: r.title,
        template_name: r.doc_template?.name ?? r.doc_template?.internal_code ?? '',
        status: r.status,
        output_format: r.output_format,
        page_count: r.page_count,
        record_id: r.record_id,
        dms_document_id: r.dms_document_id,
        created_at: r.created_at,
      })));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const templateOptions = useMemo(
    () => Array.from(new Set(rows.map(r => r.template_name).filter(Boolean))).sort().map(v => ({ value: v, label: v })),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (!q ||
        (r.title ?? '').toLowerCase().includes(q) ||
        r.template_name.toLowerCase().includes(q) ||
        (r.record_id ?? '').toLowerCase().includes(q)) &&
      (statusFacet.length === 0 || statusFacet.includes(r.status)) &&
      (formatFacet.length === 0 || (r.output_format != null && formatFacet.includes(r.output_format))) &&
      (templateFacet.length === 0 || templateFacet.includes(r.template_name))
    );
  }, [rows, filter, statusFacet, formatFacet, templateFacet]);

  const anyFilter = filter.trim() !== '' || statusFacet.length > 0 || formatFacet.length > 0 || templateFacet.length > 0;

  // §17.B13 related-children: the generated doc's parent template (resolved name; raw id never shown).
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => {
    const r = rows.find(x => x.doc_generated_id === id);
    return [
      { key: 'template', title: t('doc.generated.related.template', 'Template'),
        items: r?.template_name ? [{ id: 'template', label: r.template_name }] : [] },
    ];
  };
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  /**
   * Short-lived signed URL for a row's stored artifact.
   *
   * The DMS document id addresses the row; the STORAGE PATH addresses the bytes,
   * and only dms_document knows it — hence the lookup before signing. (This
   * replaces a call to an RPC named dms_signed_url that was never created, so
   * every Download failed with "function does not exist"; signing belongs at the
   * storage layer anyway, which is what DmsService.getSignedUrl already does
   * through the gateway.)
   */
  const artifactUrl = async (r: GeneratedRow): Promise<string> => {
    if (r.dms_document_id == null) throw new Error(NO_ARTIFACT);
    const { data, error: e } = await apiDb
      .from('dms_document')
      .select('storage_path')
      .eq('document_id', r.dms_document_id)
      .maybeSingle();
    if (e) throw new Error(e.message);
    const path = (data as { storage_path?: string } | null)?.storage_path;
    if (!path) throw new Error(NO_ARTIFACT);
    return getSignedUrl(path);
  };

  /**
   * Save the artifact. Fetches the bytes and hands them over as a named file
   * rather than window.open()-ing the signed URL: a browser set to open PDFs
   * externally turns that into a silent no-op or a stray tab, and the URL's own
   * filename is the opaque `<doc_generated_id>.pdf`. This always produces a
   * download, named after the document.
   */
  const download = async (r: GeneratedRow) => {
    setDownloadError(null);
    try {
      const url = await artifactUrl(r);
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileNameFor(r);
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke on the next tick — Safari cancels the download if the URL dies
      // while the click is still being handled.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch (e) {
      setDownloadError(messageFor(e, t));
    }
  };

  const preview = async (r: GeneratedRow) => {
    setDownloadError(null);
    try {
      setViewing({ row: r, url: await artifactUrl(r) });
    } catch (e) {
      setDownloadError(messageFor(e, t));
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('doc.generated.title', 'Generated Documents')}
          description={t('doc.generated.list.description', 'Track document render requests, watch their status and download the finished artifacts.')}
          actions={
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}
            </button>
          }
        />

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-44 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-sm focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter
              label={t('doc.generated.facet.status', 'Status')}
              options={STATUSES.map(s => ({ value: s.value, label: t(s.labelKey, s.fallback) }))}
              selected={statusFacet}
              onChange={setStatusFacet}
            />
            <FacetFilter
              label={t('doc.generated.facet.format', 'Format')}
              options={OUTPUT_FORMATS.map(f => ({ value: f.value, label: t(f.labelKey, f.fallback) }))}
              selected={formatFacet}
              onChange={setFormatFacet}
            />
            <FacetFilter
              label={t('doc.generated.facet.template', 'Template')}
              options={templateOptions}
              selected={templateFacet}
              onChange={setTemplateFacet}
            />
            <ClearFiltersButton
              active={anyFilter}
              onClear={() => { setFilter(''); setStatusFacet([]); setFormatFacet([]); setTemplateFacet([]); }}
            />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              {error && <span className="text-csm-xs text-csm-danger">{error}</span>}
              {downloadError && <span className="text-csm-xs text-csm-danger">{downloadError}</span>}
              <PersonalizationToggle />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left"
                      style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.doc_generated_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.doc_generated_id)}
                        hasChildren={hasRelated(r.doc_generated_id)}
                        onToggle={() => detail.toggle(r.doc_generated_id)}
                        expandLabel={t('common.related.show', 'Show related records')}
                        collapseLabel={t('common.related.hide', 'Hide related')}
                        tooltip={t('common.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'title'         && (r.title || <span className="italic text-csm-watermark">{t('common.none', 'None')}</span>)}
                        {col.key === 'template_name' && (r.template_name || <span className="italic text-csm-watermark">{t('common.none', 'None')}</span>)}
                        {col.key === 'status'        && (
                          <span className={
                            'inline-block rounded border px-1.5 py-0.5 text-csm-2xs uppercase tracking-wider ' +
                            (r.status === 'RENDERED' ? 'border-csm-success-border bg-csm-success-soft text-csm-success'
                              : r.status === 'FAILED' ? 'border-csm-danger-border bg-csm-danger-soft text-csm-danger'
                              : 'border-csm-border-strong bg-csm-surface-alt text-csm-muted')
                          }>{statusLabel(r.status)}</span>
                        )}
                        {col.key === 'output_format' && formatLabel(r.output_format)}
                        {col.key === 'page_count'    && (r.page_count ?? '')}
                        {col.key === 'record_id'     && (r.record_id ?? '')} {/* fk-display-ok: record_id is the natural id of the audited source row */}
                        {col.key === 'created_at'    && formatDateTime(r.created_at)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                      {r.status === 'RENDERED' ? (
                        <span className="inline-flex gap-1">
                          <button onClick={() => void preview(r)}
                            className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-surface-alt">
                            {t('doc.generated.view', 'View')}
                          </button>
                          <button onClick={() => void download(r)}
                            className="rounded border border-primary bg-primary px-2 py-0.5 text-csm-xs text-white hover:bg-primary-hover">
                            {t('doc.generated.download', 'Download')}
                          </button>
                        </span>
                      ) : (
                        <span className="text-csm-2xs text-csm-muted">—</span>
                      )}
                    </td>
                  </tr>
                  {detail.isOpen(r.doc_generated_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.doc_generated_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('doc.generated.empty', 'No generated documents yet')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {/* Read it here rather than hunting a downloaded file: a centered viewer
          (§17.B6) over the signed URL. The browser's own PDF plugin renders it,
          so Save / Print come free — and Open in new tab is there for anyone who
          would rather have the full window. */}
      {viewing && (
        <Modal
          title={viewing.row.title || t('doc.generated.preview', 'Document preview')}
          onClose={() => { setViewing(null); setExpanded(false); }}
          width={expanded ? 'full' : '4xl'}
          headerAction={
            <button type="button" onClick={() => setExpanded(e => !e)}
              className="rounded-csm-control border border-csm-border bg-white px-2 py-1 text-csm-xs hover:bg-csm-surface-alt"
              title={expanded ? t('common.collapse', 'Collapse') : t('common.expand', 'Expand')}>
              {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </button>
          }
          footer={
            /* Save and Print live in the viewer's own toolbar, beside zoom —
               a second Download here was the same action twice. */
            <button
              onClick={() => { setViewing(null); setExpanded(false); }}
              className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover">
              {t('common.close', 'Close')}
            </button>
          }
        >
          {/* Rasterised in-app: an <iframe> renders nothing when the browser is
              set to open PDFs externally, which is how this shipped blank. */}
          <div className="max-h-[70vh] overflow-auto rounded-csm-control bg-csm-surface-alt p-3">
            <PdfViewer url={viewing.url} fileName={fileNameFor(viewing.row)}
            width={expanded ? 1200 : 820} maxHeight={expanded ? '78vh' : '68vh'} />
          </div>
        </Modal>
      )}
    </div>
  );
}

export function DocGeneratedPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={GENERATED_PAGE} session={session}>
      <GeneratedInner session={session} />
    </PersonalizationProvider>
  );
}
